import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Message, MessageContent } from '@/features/chat/chatSlice';
import { sanitizeInput } from '@/utils/security';

interface ChatMessageProps {
  message: Message;
  onSuggestedActionClick?: (action: string) => void;
}

const getDisplayContent = (content: MessageContent): string => {
  // Gracefully handle unexpected or undefined content
  if (content == null) {
    return '';
  }
  if (typeof content === 'string') {
    return content;
  }
  // Narrow to object form of MessageContent via in-operator and runtime type check
  if (
    typeof content === 'object' &&
    'userFacingMessage' in content &&
    typeof (content as { userFacingMessage?: unknown }).userFacingMessage === 'string'
  ) {
    return (content as { userFacingMessage: string }).userFacingMessage;
  }
  // As a last resort, avoid crashing and return empty string
  return '';
};

const toInitials = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) {
    return 'AI';
  }
  const letters = trimmed.replace(/\s+/g, '').slice(0, 2).toUpperCase();
  return letters || 'AI';
};

export const ChatMessage = ({ message, onSuggestedActionClick }: ChatMessageProps) => {
  const { author, content, suggestedActions } = message;
  const displayContent = getDisplayContent(content);
  const isUser = author === 'user';
  const rowAlignmentClass = isUser ? 'justify-end' : '';
  const adminName = typeof message.uiPayload?.adminName === 'string'
    ? message.uiPayload.adminName
    : undefined;
  const isAdminMessage = !!adminName;
  const avatarFallback = isAdminMessage ? toInitials(adminName) : 'AI';
  const avatarAlt = isAdminMessage ? adminName : 'AI';

  return (
    <div className={`flex flex-col gap-2 p-4 ${isUser ? 'items-end' : 'items-start'}`}>
      <div className={`flex items-start gap-4 w-full ${rowAlignmentClass}`}>
        {author === 'ai' && (
          <Avatar className="h-8 w-8">
            <AvatarImage src="/ai-avatar.png" alt={avatarAlt} />
            <AvatarFallback>{avatarFallback}</AvatarFallback>
          </Avatar>
        )}
        <div
          className={`rounded-lg p-3 max-w-[75%] ${isUser ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
          <div
            className="text-sm prose dark:prose-invert prose-p:my-1 prose-p:leading-snug"
            dangerouslySetInnerHTML={{ __html: sanitizeInput(displayContent) }}
          />
        </div>
        {isUser && (
          <Avatar className="h-8 w-8">
            <AvatarImage src="/user-avatar.png" alt="User" />
            <AvatarFallback>You</AvatarFallback>
          </Avatar>
        )}
      </div>
      {author === 'ai' && suggestedActions && suggestedActions.length > 0 && (
        <div className="flex flex-wrap gap-2 pl-12">
          {suggestedActions.map((action) => (
            <Button
              key={action}
              variant="outline"
              size="sm"
              onClick={() => onSuggestedActionClick?.(action)}
            >
              {action}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};
